
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CartIcon } from "@/components/cart/CartIcon";
import { NavbarLogo } from './navbar/NavbarLogo';
import { DesktopNavigation } from './navbar/DesktopNavigation';
import { MobileNavigation } from './navbar/MobileNavigation';
import { NavbarActions } from './navbar/NavbarActions';
import { NotificationBanner } from '@/components/ui/notification-banner';
import { useNavbarScroll } from './navbar/useNavbarScroll';
import { useNavbarNavigation } from './navbar/useNavbarNavigation';
import { createNavLinks, getFilteredNavLinks } from './navbar/navLinks';
import { useNotifications } from '@/contexts/NotificationContext';
import { InfoBanner } from '@/components/InfoBanner';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isScrolled } = useNavbarScroll();
  const { handleFAQClick } = useNavbarNavigation();
  const { notifications, removeNotification } = useNotifications();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = createNavLinks(handleFAQClick);
  const filteredNavLinks = getFilteredNavLinks(navLinks);

  return (
    <>
      {/* Info banner above the sticky header */}
      <InfoBanner />
      
      <header className={`
        w-full transition-all duration-300 ease-out sticky top-0 z-50 py-4
        ${isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.08)]" 
          : "bg-white"
        }
      `}>
        <div className={`
          absolute bottom-0 left-0 right-0 h-[1px] 
          bg-gradient-to-r from-transparent via-primary/20 to-transparent
          transition-opacity duration-300
          ${isScrolled ? 'opacity-100' : 'opacity-0'}
        `} />
        
        <div className="container mx-auto px-4">
          {/* Main navigation row */}
          <div className="flex items-center">
            {/* Logo section - positioned more to the left on desktop */}
            <div className="flex-shrink-0 lg:mr-10">
              <NavbarLogo isCompact={false} />
            </div>

            {/* Desktop navigation - centered with more space */}
            <div className="hidden lg:flex flex-1 justify-center">
              <DesktopNavigation 
                filteredNavLinks={filteredNavLinks}
                handleFAQClick={handleFAQClick}
              />
            </div>

            {/* Desktop actions - positioned to the right */}
            <div className="hidden lg:flex items-center space-x-4">
              <CartIcon />
              <NavbarActions isCompact={isScrolled} />
            </div>

            {/* Mobile/Tablet: Cart icon and menu burger side by side - show on medium and below */}
            <div className="lg:hidden flex items-center space-x-2 ml-auto">
              <CartIcon />
              <MobileNavigation 
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
                filteredNavLinks={filteredNavLinks}
              />
            </div>
          </div>

          {/* Notifications row - appears below main navigation */}
          <NotificationBanner 
            notifications={notifications}
            onDismiss={removeNotification}
            className="mt-2"
          />
        </div>
      </header>
    </>
  );
};

export default Navbar;
